import { timing, variantForLevel } from './params';
const FEEDBACK_TAP_DELAY_MS = 200;
function cellEq(a, b) {
    return a.row === b.row && a.col === b.col;
}
export function buildSequence(trialIdx, length, gridSize, mode) {
    const totalCells = gridSize * gridSize;
    const safeLen = Math.max(2, Math.min(length, totalCells));
    const pool = [];
    for (let r = 0; r < gridSize; r += 1) {
        for (let c = 0; c < gridSize; c += 1)
            pool.push({ row: r, col: c });
    }
    const cells = [];
    while (cells.length < safeLen && pool.length > 0) {
        const i = Math.floor(Math.random() * pool.length);
        const picked = pool.splice(i, 1)[0];
        const last = cells[cells.length - 1];
        if (last && cellEq(last, picked)) {
            pool.push(picked);
            continue;
        }
        cells.push(picked);
    }
    const expected = mode === 'backward' ? [...cells].reverse() : cells;
    return { trialIdx, length: safeLen, gridSize, mode, cells, expected };
}
export function createMemoryGridAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        sequence: null,
        presentingIdx: -1,
        taps: [],
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function presentNextCell(seq, idx) {
        if (idx >= seq.cells.length) {
            update({ presentingIdx: -1, subPhase: 'go_signal' });
            timers.push(setTimeout(() => {
                update({ subPhase: 'awaiting_input' });
                trialStart = Date.now();
            }, timing.goSignalMs));
            return;
        }
        update({ subPhase: 'presenting', presentingIdx: idx });
        timers.push(setTimeout(() => {
            presentNextCell(seq, idx + 1);
        }, timing.cellLitMs + timing.isiMs));
    }
    function settle(correct, finalTaps) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ taps: finalTaps, correct, reactionTimeMs: rt });
    }
    return {
        mount() {
            // Idempotent — pas de DOM imperative.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(seq) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'pre_sequence',
                    sequence: seq,
                    presentingIdx: -1,
                    taps: [],
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    presentNextCell(seq, 0);
                }, timing.preSequenceMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleTap(cell) {
            if (state.subPhase !== 'awaiting_input' || !state.sequence)
                return;
            const newTaps = [...state.taps, cell];
            update({ taps: newTaps });
            const expected = state.sequence.expected;
            const correctSoFar = newTaps.every((t, i) => cellEq(t, expected[i]));
            if (!correctSoFar) {
                // Erreur — on attend FEEDBACK_TAP_DELAY_MS pour visualiser, puis settle(false).
                timers.push(setTimeout(() => {
                    settle(false, newTaps);
                }, FEEDBACK_TAP_DELAY_MS));
                return;
            }
            if (newTaps.length === expected.length) {
                // Séquence complète et correcte.
                timers.push(setTimeout(() => {
                    settle(true, newTaps);
                }, FEEDBACK_TAP_DELAY_MS));
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map